import { useEffect, useState } from 'react';

interface ConfirmButtonProps {
  label: string;
  confirmLabel: string;
  onConfirm: () => void;
  className?: string;
}

/** How long the armed state waits for the second tap before it lets go. */
const ARMED_MS = 4000;

/**
 * A button that asks twice.
 *
 * For the handful of actions that end something for somebody — the host
 * ending the round for everyone, a guest walking out of it — and that sit in
 * a corner a thumb brushes on its way to the card. The first tap only turns
 * the button into its own question; the second one acts. No dialog: the
 * question stays exactly where the finger already is, and it quietly reverts
 * if nobody answers.
 */
export function ConfirmButton({ label, confirmLabel, onConfirm, className }: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), ARMED_MS);
    return () => window.clearTimeout(timer);
  }, [armed]);

  return (
    <button
      type="button"
      className={`${className ?? 'btn btn--ghost'}${armed ? ' btn--armed' : ''}`}
      aria-live="polite"
      onClick={() => {
        if (armed) {
          setArmed(false);
          onConfirm();
        } else {
          setArmed(true);
        }
      }}
    >
      {armed ? confirmLabel : label}
    </button>
  );
}
